import { FunctionComponent } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BodyText, Div, Flex, GridBag, Gutter, Headline, HeroBannerLayout, Icon } from '@gaiads/telia-react-component-library';
import { AllowanceType, UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import { User } from '../../../../types';
import { hasUserARequiredRole, isNumeric } from '../../../../utils';
import Allowance from '../Allowance/Allowance';
import OrderingOnBehalfInput from '../OrderingOnBehalfInput/OrderingOnBehalfInput';

interface ProductListHeaderProps {
  authenticatedUser: User | null;
  ordersToApprove: number | null;
  isMobile: boolean;
  isOnBehalfOrder: boolean;
  heroImage: string;
}

export const ProductListHeader: FunctionComponent<ProductListHeaderProps> = ({
  authenticatedUser,
  isMobile,
  ordersToApprove,
  isOnBehalfOrder,
  heroImage,
}) => {
  const { t } = useTranslation('product');
  const history = useHistory();

  const showOwnAllowance =
    !isOnBehalfOrder &&
    (authenticatedUser?.allowance_type === AllowanceType.Monthly || authenticatedUser?.allowance_type === AllowanceType.OneTime) &&
    isNumeric(authenticatedUser.allowance);

  const showOrderingOnBehalfInput = hasUserARequiredRole([UserRole.Orderer]);

  if (hasUserARequiredRole([UserRole.Approver])) {
    return (
      <Div>
        <Gutter size="layout-8" />
        <GridBag>
          <GridBag.Item sm={12} md={6}>
            <Headline size="xlg" color="black" tagName="h1">
              {t('ProductListingComponent.greeting').toUpperCase()}
            </Headline>
            <Headline size="xlg" color="black" tagName="h1">
              {authenticatedUser?.given_name?.toUpperCase() || ''}
            </Headline>
            {!!ordersToApprove && (
              <>
                <BodyText margin={{ top: 'md' }} color="gray500" fontWeight="bold">
                  {t('ProductListingComponent.pendingApprovals', { amount: ordersToApprove })}
                </BodyText>
                <BodyText margin={{ top: 'md' }} color="purple500" clickableAppearance fontWeight="medium" onClick={() => history.push('/approval')}>
                  {t('ProductListingComponent.approvalsLink')}
                  <Icon tagName="span" name="chevron-right" size="sm" padding={{ top: 'xxxs' }} />
                </BodyText>
              </>
            )}
            <Gutter size={isMobile ? 'xlg' : ordersToApprove ? 'layout-8' : 'layout-9'} />
          </GridBag.Item>
          <GridBag.Item sm={12} md={6}>
            <Flex directionColumn>
              {showOwnAllowance && <Div flexItem={{ sizeMultiplier: 2 }}>{<Allowance user={authenticatedUser} />}</Div>}
              {showOrderingOnBehalfInput && (
                <Div>
                  <OrderingOnBehalfInput />
                </Div>
              )}
            </Flex>
          </GridBag.Item>
        </GridBag>
      </Div>
    );
  } else {
    return (
      <>
        <HeroBannerLayout
          heading={
            !isMobile ? (
              <>
                <Headline size="xlg" color="black" tagName="h1">
                  {t('ProductListingComponent.heroTitle1')}
                </Headline>
                <Headline size="xlg" color="black" tagName="h1">
                  {t('ProductListingComponent.heroTitle2')}
                </Headline>
              </>
            ) : (
              <Headline size="xlg" color="black" tagName="h1">
                {t('ProductListingComponent.heroTitle1')} {t('ProductListingComponent.heroTitle2')}
              </Headline>
            )
          }
          description={
            <BodyText color="gray500" fontWeight="bold" padding={{ top: 'md' }}>
              {t('ProductListingComponent.heroDescription')}
            </BodyText>
          }
          image={!isMobile ? heroImage : undefined}
          imageAlt={'Mobile phone'}
          color={'black'}
          headlineTagName={'h1'}
        />
        <GridBag>
          {showOrderingOnBehalfInput && (
            <GridBag.Item sm={12} md={6}>
              <OrderingOnBehalfInput />
            </GridBag.Item>
          )}
          {showOwnAllowance && (
            <GridBag.Item sm={12} md={6}>
              <Allowance user={authenticatedUser} />
            </GridBag.Item>
          )}
        </GridBag>
      </>
    );
  }
};
